import api, { unwrap } from './client';
import type { ApiResponse, PageResponse, SearchParams } from '@/types/api';
import type {
  CustomerSupport360,
  DeskDashboardSummary,
  DeskForm,
  DeskKbArticle,
  DeskKbCategory,
  DeskPriority,
  DeskStatus,
  DeskTeam,
  DeskTicket,
  DeskTicketBulkRequest,
  DeskTicketDetail,
  DeskSavedView,
  DeskTicketLink,
  DeskTicketRequest,
  DeskTicketUpdateRequest,
  DeskTimeEntry,
  DeskTimeEntryRequest,
} from '@/types/desk';

export const deskApi = {
  dashboardSummary: (period: import('@/types/dashboardPresentation').DashboardPeriod = 'MONTH') =>
    unwrap(api.get<ApiResponse<DeskDashboardSummary>>('/v1/desk/dashboard/summary', { params: { period } })),

  support360: (customerId: number) =>
    unwrap(api.get<ApiResponse<CustomerSupport360>>(`/v1/desk/customers/${customerId}/support-360`)),

  listStatuses: () =>
    unwrap(api.get<ApiResponse<DeskStatus[]>>('/v1/desk/statuses')),

  createStatus: (body: Partial<DeskStatus>) =>
    unwrap(api.post<ApiResponse<DeskStatus>>('/v1/desk/statuses', body)),

  listPriorities: () =>
    unwrap(api.get<ApiResponse<DeskPriority[]>>('/v1/desk/priorities')),

  createPriority: (body: Partial<DeskPriority>) =>
    unwrap(api.post<ApiResponse<DeskPriority>>('/v1/desk/priorities', body)),

  listTeams: () =>
    unwrap(api.get<ApiResponse<DeskTeam[]>>('/v1/desk/teams')),

  createTeam: (body: Partial<DeskTeam>) =>
    unwrap(api.post<ApiResponse<DeskTeam>>('/v1/desk/teams', body)),

  listForms: () =>
    unwrap(api.get<ApiResponse<DeskForm[]>>('/v1/desk/forms')),

  createForm: (body: { name: string; code: string; active?: boolean; fields?: Array<Record<string, unknown>> }) =>
    unwrap(api.post<ApiResponse<DeskForm>>('/v1/desk/forms', body)),

  listKbCategories: () =>
    unwrap(api.get<ApiResponse<DeskKbCategory[]>>('/v1/desk/kb/categories')),

  createKbCategory: (body: { name: string; parentCategoryId?: number }) =>
    unwrap(api.post<ApiResponse<DeskKbCategory>>('/v1/desk/kb/categories', body)),

  listKbArticles: (params?: { search?: string; publishedOnly?: boolean }) =>
    unwrap(api.get<ApiResponse<DeskKbArticle[]>>('/v1/desk/kb/articles', { params })),

  createKbArticle: (body: { title: string; content: string; categoryId?: number; published?: boolean }) =>
    unwrap(api.post<ApiResponse<DeskKbArticle>>('/v1/desk/kb/articles', body)),

  updateKbArticle: (
    id: number,
    body: { title: string; content: string; categoryId?: number; published?: boolean; internalOnly?: boolean },
  ) => unwrap(api.put<ApiResponse<DeskKbArticle>>(`/v1/desk/kb/articles/${id}`, body)),

  publishKbArticle: (id: number, published = true) =>
    unwrap(api.post<ApiResponse<DeskKbArticle>>(`/v1/desk/kb/articles/${id}/publish`, null, { params: { published } })),

  deleteKbArticle: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/desk/kb/articles/${id}`)),

  listKbArticleVersions: (id: number) =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskKbArticleVersion[]>>(`/v1/desk/kb/articles/${id}/versions`)),

  submitKbFeedback: (id: number, body: { ticketId?: number; customerId?: number; helpful?: boolean; feedback?: string }) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/desk/kb/articles/${id}/feedback`, body)),

  ticketKbSuggestions: (ticketId: number) =>
    unwrap(api.get<ApiResponse<DeskKbArticle[]>>(`/v1/desk/tickets/${ticketId}/kb-suggestions`)),

  listTickets: (params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<DeskTicket>>>('/v1/desk/tickets', { params })),

  listQueueTickets: (queue: string, params?: SearchParams) =>
    unwrap(api.get<ApiResponse<PageResponse<DeskTicket>>>(`/v1/desk/workspace/queues/${queue}/tickets`, { params })),

  listSavedViews: () =>
    unwrap(api.get<ApiResponse<DeskSavedView[]>>('/v1/desk/workspace/saved-views')),

  createSavedView: (body: { name: string; filters?: Record<string, unknown>; defaultView?: boolean }) =>
    unwrap(api.post<ApiResponse<DeskSavedView>>('/v1/desk/workspace/saved-views', body)),

  deleteSavedView: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/desk/workspace/saved-views/${id}`)),

  getTicket: (id: number, viewerEmployeeId?: number) =>
    unwrap(api.get<ApiResponse<DeskTicketDetail>>(`/v1/desk/tickets/${id}`, {
      params: viewerEmployeeId != null ? { viewerEmployeeId } : undefined,
    })),

  createTicket: (body: DeskTicketRequest) =>
    unwrap(api.post<ApiResponse<DeskTicket>>('/v1/desk/tickets', body)),

  updateTicket: (id: number, body: DeskTicketUpdateRequest) =>
    unwrap(api.put<ApiResponse<DeskTicket>>(`/v1/desk/tickets/${id}`, body)),

  deleteTicket: (id: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/desk/tickets/${id}`)),

  bulkUpdateTickets: (body: DeskTicketBulkRequest) =>
    unwrap(api.post<ApiResponse<DeskTicket[]>>('/v1/desk/tickets/bulk', body)),

  assignTicket: (id: number, assignedEmployeeId: number) =>
    unwrap(api.post<ApiResponse<DeskTicket>>(`/v1/desk/tickets/${id}/assign`, { assignedEmployeeId })),

  transitionTicket: (id: number, statusId: number) =>
    unwrap(api.post<ApiResponse<DeskTicket>>(`/v1/desk/tickets/${id}/transition`, { statusId })),

  addComment: (id: number, commentText: string, internalNote = false) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/desk/tickets/${id}/comments`, { commentText, internalNote })),

  addTicketTag: (id: number, tag: string) =>
    unwrap(api.post<ApiResponse<DeskTicket>>(`/v1/desk/tickets/${id}/tags`, { tag })),

  removeTicketTag: (id: number, tag: string) =>
    unwrap(api.delete<ApiResponse<DeskTicket>>(`/v1/desk/tickets/${id}/tags/${encodeURIComponent(tag)}`)),

  updateTicketCustomFields: (id: number, customFields: Record<string, string>) =>
    unwrap(api.put<ApiResponse<DeskTicket>>(`/v1/desk/tickets/${id}/custom-fields`, { customFields })),

  addTicketLink: (id: number, body: { linkedTicketId: number; linkType?: string }) =>
    unwrap(api.post<ApiResponse<DeskTicketLink>>(`/v1/desk/tickets/${id}/links`, body)),

  removeTicketLink: (id: number, linkId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/desk/tickets/${id}/links/${linkId}`)),

  addTicketFollower: (ticketId: number, employeeId: number) =>
    unwrap(api.post<ApiResponse<import('@/types/desk').DeskFollower>>(
      `/v1/desk/workspace/tickets/${ticketId}/followers`,
      { employeeId },
    )),

  removeTicketFollower: (ticketId: number, employeeId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/desk/workspace/tickets/${ticketId}/followers/${employeeId}`)),

  listTimeEntries: (ticketId: number) =>
    unwrap(api.get<ApiResponse<DeskTimeEntry[]>>(`/v1/desk/tickets/${ticketId}/time-entries`)),

  listTicketAssets: (ticketId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskTicketAsset[]>>(`/v1/desk/tickets/${ticketId}/assets`)),

  addTicketAsset: (
    ticketId: number,
    body: Partial<import('@/types/desk').DeskTicketAsset>,
  ) => unwrap(api.post<ApiResponse<import('@/types/desk').DeskTicketAsset>>(`/v1/desk/tickets/${ticketId}/assets`, body)),

  deleteTicketAsset: (ticketId: number, assetLinkId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/desk/tickets/${ticketId}/assets/${assetLinkId}`)),

  listProjectLinks: (ticketId: number) =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskProjectLink[]>>(`/v1/desk/tickets/${ticketId}/projects`)),

  addProjectLink: (
    ticketId: number,
    body: Partial<import('@/types/desk').DeskProjectLink>,
  ) => unwrap(api.post<ApiResponse<import('@/types/desk').DeskProjectLink>>(`/v1/desk/tickets/${ticketId}/projects`, body)),

  addTimeEntry: (ticketId: number, body: DeskTimeEntryRequest) =>
    unwrap(api.post<ApiResponse<DeskTimeEntry>>(`/v1/desk/tickets/${ticketId}/time-entries`, body)),

  startTimer: (ticketId: number, body: DeskTimeEntryRequest) =>
    unwrap(api.post<ApiResponse<DeskTimeEntry>>(`/v1/desk/tickets/${ticketId}/time-entries/timer/start`, body)),

  stopTimer: (ticketId: number, entryId: number) =>
    unwrap(api.post<ApiResponse<DeskTimeEntry>>(`/v1/desk/tickets/${ticketId}/time-entries/${entryId}/timer/stop`)),

  deleteTimeEntry: (ticketId: number, entryId: number) =>
    unwrap(api.delete<ApiResponse<null>>(`/v1/desk/tickets/${ticketId}/time-entries/${entryId}`)),

  submitCsat: (id: number, rating: number, feedback?: string) =>
    unwrap(api.post<ApiResponse<null>>(`/v1/desk/tickets/${id}/csat`, { rating, feedback })),

  reports: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskReportsBundle>>('/v1/desk/reports')),

  listScheduledReports: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskScheduledReport[]>>('/v1/desk/reports/scheduled')),

  createScheduledReport: (body: {
    name: string;
    reportType?: string;
    format?: string;
    cronExpression?: string;
    recipientEmail?: string;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<import('@/types/desk').DeskScheduledReport>>('/v1/desk/reports/scheduled', body)),

  listSlaPolicies: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskSlaPolicy[]>>('/v1/desk/setup/sla-policies')),

  createSlaPolicy: (body: {
    name: string;
    responseMinutes?: number;
    resolutionMinutes?: number;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<import('@/types/desk').DeskSlaPolicy>>('/v1/desk/setup/sla-policies', body)),

  listEscalationRules: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskEscalationRule[]>>('/v1/desk/setup/escalation-rules')),

  createEscalationRule: (body: {
    ruleName: string;
    triggerType: string;
    thresholdMinutes?: number;
    escalateToEmployeeId?: number;
    escalateToTeamId?: number;
    priorityOrder?: number;
    active?: boolean;
  }) =>
    unwrap(
      api.post<ApiResponse<import('@/types/desk').DeskEscalationRule>>('/v1/desk/setup/escalation-rules', body),
    ),

  listWebhookSources: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskWebhookSource[]>>('/v1/desk/setup/webhook-sources')),

  createWebhookSource: (body: { sourceCode: string; secretToken?: string; active?: boolean }) =>
    unwrap(api.post<ApiResponse<import('@/types/desk').DeskWebhookSource>>('/v1/desk/setup/webhook-sources', body)),

  listCategories: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskCategory[]>>('/v1/desk/setup/categories')),

  listAutomationRules: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskAutomationRule[]>>('/v1/desk/setup/automation-rules')),

  createAutomationRule: (body: {
    name: string;
    triggerEvent: string;
    conditionJson?: string;
    active?: boolean;
    actions?: Array<{ actionType: string; actionJson?: string; sortOrder?: number }>;
  }) => unwrap(api.post<ApiResponse<import('@/types/desk').DeskAutomationRule>>('/v1/desk/setup/automation-rules', body)),

  listInboxConversations: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskInboxConversation[]>>('/v1/desk/inbox/conversations')),

  listInboxMessages: (conversationId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/desk').DeskInboxMessage[]>>(
        `/v1/desk/inbox/conversations/${conversationId}/messages`,
      ),
    ),

  board: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskBoardColumn[]>>('/v1/desk/boards')),

  getMailbox: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskMailbox>>('/v1/desk/mailboxes')),

  getBusinessCalendar: () =>
    unwrap(
      api.get<ApiResponse<import('@/types/desk').DeskBusinessCalendar>>('/v1/desk/setup/business-calendar'),
    ),

  updateBusinessCalendar: (body: {
    name?: string;
    timezone: string;
    workingDays?: string[];
    workStart?: string;
    workEnd?: string;
  }) =>
    unwrap(
      api.put<ApiResponse<import('@/types/desk').DeskBusinessCalendar>>('/v1/desk/setup/business-calendar', body),
    ),

  listHolidays: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskHoliday[]>>('/v1/desk/setup/holidays')),

  addHoliday: (body: { holidayDate: string; name?: string }) =>
    unwrap(api.post<ApiResponse<import('@/types/desk').DeskHoliday>>('/v1/desk/setup/holidays', body)),

  deleteHoliday: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/desk/setup/holidays/${id}`)),

  updateMailboxImap: (body: {
    imapHost?: string;
    imapPort?: number;
    imapUsername?: string;
    imapPassword?: string;
    syncEnabled?: boolean;
  }) => unwrap(api.put<ApiResponse<import('@/types/desk').DeskMailbox>>('/v1/desk/mailboxes/imap', body)),

  syncMailbox: () =>
    unwrap(api.post<ApiResponse<{ processed: number }>>('/v1/desk/mailboxes/sync')),

  listWorkflows: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskWorkflow[]>>('/v1/desk/workflows')),

  createWorkflow: (body: { name: string; active?: boolean }) =>
    unwrap(api.post<ApiResponse<import('@/types/desk').DeskWorkflow>>('/v1/desk/workflows', body)),

  listWorkflowTransitions: (workflowId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/desk').DeskWorkflowTransition[]>>(
        `/v1/desk/workflows/${workflowId}/transitions`,
      ),
    ),

  createWorkflowTransition: (
    workflowId: number,
    body: {
      fromStatusId?: number;
      toStatusId?: number;
      allowedRoleCode?: string;
      active?: boolean;
    },
  ) =>
    unwrap(
      api.post<ApiResponse<import('@/types/desk').DeskWorkflowTransition>>(
        `/v1/desk/workflows/${workflowId}/transitions`,
        body,
      ),
    ),

  listCannedResponses: () =>
    unwrap(api.get<ApiResponse<import('@/types/desk').DeskCannedResponse[]>>('/v1/desk/canned-responses')),

  createCannedResponse: (body: {
    title: string;
    shortcutCode?: string;
    bodyText: string;
    categoryCode?: string;
    active?: boolean;
  }) => unwrap(api.post<ApiResponse<import('@/types/desk').DeskCannedResponse>>('/v1/desk/canned-responses', body)),

  updateCannedResponse: (
    id: number,
    body: {
      title: string;
      shortcutCode?: string;
      bodyText: string;
      categoryCode?: string;
      active?: boolean;
    },
  ) => unwrap(api.put<ApiResponse<import('@/types/desk').DeskCannedResponse>>(`/v1/desk/canned-responses/${id}`, body)),

  deleteCannedResponse: (id: number) =>
    unwrap(api.delete<ApiResponse<void>>(`/v1/desk/canned-responses/${id}`)),

  listAttachments: (ticketId: number) =>
    unwrap(
      api.get<ApiResponse<import('@/types/desk').DeskAttachment[]>>(`/v1/desk/tickets/${ticketId}/attachments`),
    ),

  addAttachment: (
    ticketId: number,
    body: { fileUrl: string; fileName?: string; mimeType?: string; fileSize?: number },
  ) =>
    unwrap(
      api.post<ApiResponse<import('@/types/desk').DeskAttachment>>(
        `/v1/desk/tickets/${ticketId}/attachments`,
        body,
      ),
    ),

  replyInboxConversation: (conversationId: number, messageContent: string) =>
    unwrap(
      api.post<ApiResponse<import('@/types/desk').DeskInboxMessage>>(
        `/v1/desk/inbox/conversations/${conversationId}/reply`,
        { messageContent },
      ),
    ),

  exportReport: (format: 'CSV' | 'PDF' = 'CSV') =>
    api.get('/v1/desk/reports/export', { params: { format }, responseType: 'blob' }),
};
